import { shuffleChoices } from '@util/FlowUtils';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { isEmpty, isNonEmptyString } from 'ts-utils';
import { Checkbox, CheckboxGroup } from '../../functional/OptionCheckbox';
import { QuestionWrapper } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { InfoBox } from '../../styles/InfoBox';
import {
  autoWidth,
  CHECKBOX_TWO_COLUMN_MAX_CHARS,
  choiceLabel,
  fitsTwoColumns,
} from '../../styles/OptionButton';
import { BodySM, Heading3 } from '../../styles/Typography';
import { LayoutActions } from './LayoutActions';
import { ChoiceItem, SelectLayoutConfig } from './SingleSelectLayout';

export type MultiSelectLayoutProps = SelectLayoutConfig & {
  primaryButtonDisabled?: boolean;
  selected?: string[];
  onPressPrimaryButton?: (value: string[]) => void;
  primaryButtonTitle: string;
  onShowToast?: () => void;
  /** Choices that clear every other selection when picked, e.g. "None". */
  exclusiveChoices?: string[];
};

export const MultiSelectLayout: FunctionComponent<MultiSelectLayoutProps> = (props) => {
  const {
    heading,
    body,
    bodyAlign,
    choices: unshuffledChoices,
    selected: preSelected = [],
    randomizeChoices,
    primaryButtonTitle,
    onPressPrimaryButton,
    animationEnabled = true,
    toastMessage,
    onShowToast,
    infoBox,
    exclusiveChoices,
  } = props;

  const choices = useMemo(
    () => (randomizeChoices ? shuffleChoices(unshuffledChoices) : unshuffledChoices),
    []
  );

  const [selected, setSelected] = useState<string[]>(preSelected);

  function onPrimaryPress() {
    if (isNonEmptyString(toastMessage)) onShowToast?.();
    onPressPrimaryButton?.(selected);
  }

  return (
    <QuestionWrapper>
      <div style={{ margin: 'auto' }}>
        <CopyContainer style={bodyAlign === undefined ? undefined : { textAlign: bodyAlign }}>
          {!isEmpty(heading) && <Heading3>{heading}</Heading3>}
          {body?.map((text: string, index: number) => (
            <BodySM key={index} data-test="surveyQuestion">
              {text}
            </BodySM>
          ))}
        </CopyContainer>
        <CheckboxGroup
          defaultSelected={selected}
          onChange={setSelected}
          exclusiveChoices={exclusiveChoices}
          twoColumns={fitsTwoColumns(choices, CHECKBOX_TWO_COLUMN_MAX_CHARS)}
        >
          {choices.map((choice: ChoiceItem, index: number) => {
            const label = choiceLabel(choice);
            const value = typeof choice === 'object' ? choice.value : choice;

            return (
              <Checkbox
                key={index}
                width={autoWidth(choices)}
                value={value}
                animationEnabled={animationEnabled}
                animationIndex={index}
              >
                {label}
              </Checkbox>
            );
          })}
        </CheckboxGroup>
        {infoBox && <InfoBox {...infoBox} />}
      </div>
      <LayoutActions
        primaryButtonTitle={primaryButtonTitle}
        onPressPrimaryButton={onPrimaryPress}
        primaryButtonDisabled={selected.length === 0}
      />
    </QuestionWrapper>
  );
};
